// iter-119bk Phase 10 — Playbook signal emission helper.
//
// Records user customisations (widget add/remove/rename, KPI edits)
// made to dashboards that originated from a canonical playbook /
// gallery template. Each call POSTs to `/api/playbook-signals` —
// the backend dedups within a 5-minute window so accidental double
// fires don't inflate counts.
//
// All functions accept the dashboard doc directly so callers don't
// need to know about `source_gallery_template_id`. When the dashboard
// has NO source template (= user-built from scratch), every helper
// silently no-ops — Phase 10 only learns from drift on canonical
// playbooks.
//
// Errors are swallowed: telemetry must never block a user action.

import { api } from "@/lib/api";

function templateId(dashboard) {
    return (dashboard && (dashboard.source_gallery_template_id || dashboard.template_id)) || null;
}

async function emit(dashboard, signal_kind, payload) {
    const tpl = templateId(dashboard);
    if (!tpl) return; // not a cloned template — nothing to learn from
    try {
        await api.post("/playbook-signals", {
            template_id: tpl,
            dashboard_id: dashboard.id || null,
            signal_kind,
            payload: payload || {},
        });
    } catch {
        // Telemetry must never break a user-facing flow.
    }
}

export function recordWidgetAdded(dashboard, widget) {
    return emit(dashboard, "widget_added", {
        widget_id: widget?.id,
        widget_type: widget?.type || widget?.config?.chart_type,
        widget_title: widget?.title,
    });
}

export function recordWidgetRemoved(dashboard, widget) {
    return emit(dashboard, "widget_removed", {
        widget_id: widget?.id,
        widget_type: widget?.type || widget?.config?.chart_type,
        widget_title: widget?.title,
    });
}

export function recordWidgetRenamed(dashboard, widget, newTitle) {
    return emit(dashboard, "widget_renamed", {
        widget_id: widget?.id,
        widget_title: widget?.title,
        new_title: newTitle,
    });
}

export function recordKpiSwapped(dashboard, previousWidget, replacementWidget) {
    return emit(dashboard, "kpi_renamed", {
        widget_id: previousWidget?.id,
        kpi_id: previousWidget?.id,
        previous_title: previousWidget?.title,
        new_title: replacementWidget?.title,
        kind: previousWidget?.type || previousWidget?.config?.chart_type,
    });
}

export function recordKpiFormulaEdited(dashboard, widget, newFormula) {
    return emit(dashboard, "kpi_formula_edited", {
        widget_id: widget?.id,
        kpi_id: widget?.id,
        previous_formula: widget?.config?.formula,
        formula: newFormula,
    });
}

export function recordFilterAdded(dashboard, filterField) {
    return emit(dashboard, "filter_added", { filter_field: filterField });
}

export function recordFilterRemoved(dashboard, filterField) {
    return emit(dashboard, "filter_removed", { filter_field: filterField });
}
